export type TrustpilotGlobal = {
  loadFromElement: (element: Element, render: boolean) => void;
};

type WindowWithTrustpilot = Window & {
  Trustpilot?: TrustpilotGlobal;
};

/**
 * How often the poller re-checks while a widget is still missing its iframe. Kept at or below
 * the first retry delay, otherwise the poll cadence — not the backoff — sets the real pace.
 */
const POLL_MS = 150;

/**
 * Cadence while `window.Trustpilot` does not exist yet. Nothing can be called in that state,
 * and on a saturated main thread — an empty-cache hard reload on Windows being the worst case
 * measured — a fast poll only adds to the contention it is waiting on.
 */
const BOOTSTRAP_POLL_MS = 500;

/**
 * How long to wait for the bootstrap to turn up at all. Deliberately separate from the render
 * budget: a bootstrap that arrives late must still find a live watcher, rather than one that
 * already expired while there was nothing it could do.
 */
const BOOTSTRAP_WAIT_MS = 120_000;

/**
 * How long to keep trying *once the bootstrap exists*. Cookiebot can release the bootstrap tag
 * late and Trustpilot's own auto-init can lose the race on a warm cache, so the window has to
 * cover both.
 *
 * This is measured from the moment `window.Trustpilot` is first seen, not from mount. Anchoring
 * it to mount is what produced the hard-reload failure: the budget expired while the bootstrap
 * was still loading, so the watcher stopped having made zero `loadFromElement` calls, and the
 * widget stayed blank permanently even though everything it needed arrived moments later.
 */
const TOTAL_BUDGET_MS = 30_000;

/**
 * Trustpilot's bootstrap normally renders widgets by itself; forcing a load immediately would
 * race it and can produce a visible flash. Give it this long to win before we intervene.
 */
const AUTO_INIT_GRACE_MS = 750;

/**
 * After a widget first shows an iframe, keep watching for this long rather than declaring
 * success and walking away.
 *
 * Now that the bootstrap ships in the server HTML it can inject the iframe *before* React
 * hydrates that subtree, and hydration then discards it — the widget renders and silently
 * vanishes. Measured on Windows, where hydration lands after the injection; a faster Mac
 * hydrates first and never sees it, which is the whole of the platform difference.
 */
const POST_RENDER_WATCH_MS = 15_000;

/** Cadence of that post-render watch. Deliberately cheap: it only re-checks for a live iframe. */
const POST_RENDER_POLL_MS = 250;

/**
 * `loadFromElement` is not reliably effective on the first call — on a cached load the
 * bootstrap object can exist before it is ready to build iframes. Each widget therefore gets
 * repeated attempts, spaced by a ramp: quick retries recover the common case in a few hundred
 * milliseconds, and the widening gaps keep a genuinely late bootstrap from being hammered.
 *
 * There is deliberately no attempt ceiling. A fixed count (previously 6, spaced a flat second
 * apart) was the failure mode: widgets were routinely succeeding on their last one or two
 * attempts, so any extra delay — a slower device, a busy main thread, a back/forward restore —
 * exhausted the count and left the widget blank for good with no error. `TOTAL_BUDGET_MS` is
 * the only bound now, so degraded conditions produce a late widget rather than no widget.
 */
const LOAD_RETRY_DELAYS_MS = [150, 300, 600, 1_200, 2_400, 4_000] as const;

/** Gap to wait before the next `loadFromElement` for a widget that has had `attempts` already. */
function loadRetryDelayMs(attempts: number): number {
  const index = Math.min(Math.max(attempts - 1, 0), LOAD_RETRY_DELAYS_MS.length - 1);
  return LOAD_RETRY_DELAYS_MS[index];
}

/**
 * Cookiebot's auto-blocking marker. It moves the real URL of a blocked script or
 * iframe into this attribute, so its presence is the unambiguous signal that an
 * element is inert rather than merely still loading.
 */
const COOKIEBLOCK_SRC_ATTRIBUTE = 'data-cookieblock-src';

const isCookiebotBlocked = (el: Element): boolean => el.hasAttribute(COOKIEBLOCK_SRC_ATTRIBUTE);

/**
 * A widget counts as rendered only when it holds an iframe Cookiebot has not
 * neutralised — a blocked iframe is present in the DOM but will never paint.
 */
export const hasTrustpilotIframe = (el: HTMLElement): boolean =>
  [...el.querySelectorAll('iframe')].some((iframe) => !isCookiebotBlocked(iframe));

export function parseTrustpilotStyleHeight(html: string): string | null {
  const match = html.match(/data-style-height=["']([^"']+)["']/i);
  const height = match?.[1];
  return height && height !== 'auto' ? height : null;
}

export function applyTrustpilotHostHeight(host: HTMLElement, html: string): void {
  const height = parseTrustpilotStyleHeight(html);
  if (height) {
    host.style.minHeight = height;
  }
  host.style.overflowAnchor = 'none';
}

/**
 * Run a DOM mutation without letting the browser scroll the page (e.g. iframe focus).
 * One-shot restore only — does not keep fighting the user's scroll.
 */
function withoutScrollJump(fn: () => void): void {
  if (typeof window === 'undefined') {
    fn();
    return;
  }

  const x = window.scrollX;
  const y = window.scrollY;

  fn();

  if (window.scrollX !== x || window.scrollY !== y) {
    window.scrollTo(x, y);
  }

  // Trustpilot may focus the iframe on the next frame
  requestAnimationFrame(() => {
    if (window.scrollX !== x || window.scrollY !== y) {
      window.scrollTo(x, y);
    }
  });
}

function prepareWidgetShell(el: HTMLElement): void {
  const height = el.getAttribute('data-style-height');
  if (height && height !== 'auto') {
    el.style.minHeight = el.style.minHeight || height;
  }
  el.style.overflowAnchor = 'none';
}

function hardenIframes(el: HTMLElement): void {
  const height = el.getAttribute('data-style-height');
  el.querySelectorAll('iframe').forEach((iframe) => {
    iframe.loading = 'eager';
    iframe.removeAttribute('loading');
    // Keep iframe out of the focus order so the browser does not scroll it into view
    iframe.tabIndex = -1;
    iframe.setAttribute('tabindex', '-1');
    iframe.setAttribute('scrolling', 'no');
    if (height && height !== 'auto') {
      iframe.style.height = height;
      iframe.style.minHeight = height;
    }
    if (!iframe.style.width) {
      iframe.style.width = el.getAttribute('data-style-width') || '100%';
    }
  });
}

/**
 * Discard iframes Cookiebot has neutralised so `loadFromElement` can build a fresh
 * one. Leaving the inert node in place would make the widget look already rendered.
 */
function dropBlockedTrustpilotIframes(el: HTMLElement): void {
  el.querySelectorAll('iframe').forEach((iframe) => {
    if (isCookiebotBlocked(iframe)) iframe.remove();
  });
}

/** A widget is only ever left `inert` while a load attempt is genuinely in flight. */
function releaseWidget(el: HTMLElement): void {
  el.removeAttribute('inert');
}

function markRendered(el: HTMLElement): void {
  el.dataset.tpLoaded = '1';
  delete el.dataset.tpLoadAttempts;
  delete el.dataset.tpLastAttempt;
  releaseWidget(el);
  hardenIframes(el);
}

/** Clear `inert` from every widget under `root`, whatever state it ended up in. */
function releaseAllWidgets(root: ParentNode): void {
  root.querySelectorAll('.trustpilot-widget').forEach((el) => releaseWidget(el as HTMLElement));
}

/**
 * Init widgets that still have no iframe, retrying each one on a widening delay until the
 * caller's time budget expires.
 *
 * Returns true only when every widget under `root` is showing a live iframe.
 */
export function loadUnrenderedTrustpilotWidgets(
  tp: TrustpilotGlobal,
  root: ParentNode = document
): boolean {
  const widgets = root.querySelectorAll('.trustpilot-widget');
  if (widgets.length === 0) return false;

  const now = Date.now();

  withoutScrollJump(() => {
    widgets.forEach((element) => {
      const el = element as HTMLElement;
      prepareWidgetShell(el);

      if (hasTrustpilotIframe(el)) {
        markRendered(el);
        return;
      }

      // A blocked iframe never paints — remove it so a fresh one can be built.
      dropBlockedTrustpilotIframes(el);

      const attempts = Number(el.dataset.tpLoadAttempts ?? '0');
      const lastAttempt = Number(el.dataset.tpLastAttempt ?? '0');

      // Trustpilot may still be building the iframe from the previous call. Bounded by the
      // caller's time budget rather than an attempt count, so we never give up early.
      if (attempts > 0 && now - lastAttempt < loadRetryDelayMs(attempts)) {
        return;
      }

      el.dataset.tpLoadAttempts = String(attempts + 1);
      el.dataset.tpLastAttempt = String(now);
      // Block focus while Trustpilot injects the iframe (prevents scroll-into-view)
      el.setAttribute('inert', '');

      try {
        tp.loadFromElement(el, true);
      } catch {
        releaseWidget(el);
        return;
      }

      if (hasTrustpilotIframe(el)) {
        markRendered(el);
      } else {
        // The iframe may still arrive asynchronously; do not hold the widget inert for it.
        releaseWidget(el);
      }
    });
  });

  // Recomputed from the DOM so one widget's success cannot mask another's failure.
  return [...widgets].every((el) => hasTrustpilotIframe(el as HTMLElement));
}

/**
 * Retry until `window.Trustpilot` exists, then keep initialising widgets under `root` until
 * every one of them is showing an iframe or the budget runs out.
 */
export function initTrustpilotAfterBootstrap(root: ParentNode = document): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let observer: MutationObserver | undefined;

  const startedAt = Date.now();
  const bootstrapDeadline = startedAt + BOOTSTRAP_WAIT_MS;
  /** Set when `window.Trustpilot` is first seen; the render budget runs from there. */
  let bootstrapSeenAt: number | null = null;
  let renderDeadline: number | null = null;
  /** Set when every widget is showing a live iframe; cleared if one is later taken away. */
  let renderedAt: number | null = null;

  // Last scroll position from the user. Used only to undo Trustpilot iframe focus
  // scrolling — never locks the page and never fights browser scroll restoration.
  let stableX = window.scrollX;
  let stableY = window.scrollY;

  const rememberUserScroll = () => {
    stableX = window.scrollX;
    stableY = window.scrollY;
  };

  const hardenRoot = () => {
    withoutScrollJump(() => {
      root.querySelectorAll('.trustpilot-widget').forEach((el) => {
        const widget = el as HTMLElement;
        prepareWidgetShell(widget);
        if (hasTrustpilotIframe(widget)) {
          markRendered(widget);
        }
      });
    });
  };

  const allWidgetsHaveIframes = (): boolean => {
    const widgets = root.querySelectorAll('.trustpilot-widget');
    if (widgets.length === 0) return false;
    return [...widgets].every((el) => hasTrustpilotIframe(el as HTMLElement));
  };

  // Trustpilot's above-the-fold iframe often steals focus → browser scrolls it into view.
  const onFocusIn = (event: FocusEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('.trustpilot-widget')) return;
    target.blur();
    if (window.scrollX !== stableX || window.scrollY !== stableY) {
      window.scrollTo(stableX, stableY);
    }
  };

  const stop = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    releaseAllWidgets(root);
  };

  /**
   * Say so once when we give up. A silently blank widget is indistinguishable from one that was
   * never authored, and "the bootstrap never arrived" is a different bug from "the bootstrap
   * arrived but rendering failed" — so the two are reported separately.
   */
  const warnUnrendered = (reason: string) => {
    const unrendered = [...root.querySelectorAll('.trustpilot-widget')].filter(
      (el) => !hasTrustpilotIframe(el as HTMLElement)
    ).length;
    if (unrendered > 0) {
      console.warn(`[trustpilot] ${unrendered} widget(s) unrendered — ${reason}.`);
    }
  };

  const tick = () => {
    if (cancelled) return;

    const tp = (window as WindowWithTrustpilot).Trustpilot;

    // No bootstrap yet: there is nothing to call, so wait on the slow cadence and do not let
    // the render budget run. Burning it here is what left the widget blank on a hard reload.
    if (!tp) {
      if (Date.now() < bootstrapDeadline) {
        timer = setTimeout(tick, BOOTSTRAP_POLL_MS);
      } else {
        warnUnrendered(`bootstrap never loaded within ${BOOTSTRAP_WAIT_MS}ms`);
        stop();
      }
      return;
    }

    if (bootstrapSeenAt === null) {
      bootstrapSeenAt = Date.now();
      renderDeadline = bootstrapSeenAt + TOTAL_BUDGET_MS;
    }

    // Rendered and still rendered: cheap watch only. Skipping `hardenRoot` here matters —
    // it does scroll-restoring DOM work, which has no business running every 250ms.
    if (renderedAt !== null && allWidgetsHaveIframes()) {
      if (Date.now() - renderedAt < POST_RENDER_WATCH_MS) {
        timer = setTimeout(tick, POST_RENDER_POLL_MS);
      } else {
        stop();
      }
      return;
    }

    hardenRoot();

    if (allWidgetsHaveIframes()) {
      // First render. Do not declare victory: hydration may still discard this iframe (see
      // POST_RENDER_WATCH_MS). Keep watching so that we can put it back if it does.
      renderedAt = Date.now();
      timer = setTimeout(tick, POST_RENDER_POLL_MS);
      return;
    }

    // We had a live iframe and no longer do — the hydration wipe. The replacement element is a
    // fresh node carrying none of our flags, so clear stale state and start a clean pass. The
    // bootstrap's own auto-init has already had its turn on this page and will not run again,
    // so skip the grace and rebuild immediately.
    if (renderedAt !== null) {
      renderedAt = null;
      resetUnrenderedTrustpilotWidgets(root);
      bootstrapSeenAt = Date.now() - AUTO_INIT_GRACE_MS;
      renderDeadline = Date.now() + TOTAL_BUDGET_MS;
    }

    // Let Trustpilot's own auto-init win first; only then force the load. Timed from when the
    // bootstrap appeared, which is the only moment the grace means anything.
    if (
      Date.now() - bootstrapSeenAt >= AUTO_INIT_GRACE_MS &&
      loadUnrenderedTrustpilotWidgets(tp, root)
    ) {
      renderedAt = Date.now();
      timer = setTimeout(tick, POST_RENDER_POLL_MS);
      return;
    }

    if (renderDeadline !== null && Date.now() < renderDeadline) {
      timer = setTimeout(tick, POLL_MS);
      return;
    }

    warnUnrendered(`bootstrap loaded but rendering did not complete within ${TOTAL_BUDGET_MS}ms`);
    stop();
  };

  window.addEventListener('focusin', onFocusIn, true);
  window.addEventListener('wheel', rememberUserScroll, { passive: true });
  window.addEventListener('touchmove', rememberUserScroll, { passive: true });
  window.addEventListener('keydown', rememberUserScroll, { passive: true });

  if (root instanceof Element || root instanceof Document) {
    const observeTarget = root instanceof Document ? root.documentElement : root;
    // Only re-harden on DOM changes. Restoring scroll from here would fight the browser's
    // own scroll restoration on a back/forward navigation.
    observer = new MutationObserver(() => {
      if (cancelled) return;
      hardenRoot();
    });
    observer.observe(observeTarget, { childList: true, subtree: true });
  }

  timer = setTimeout(tick, 0);

  return () => {
    cancelled = true;
    stop();
    observer?.disconnect();
    window.removeEventListener('focusin', onFocusIn, true);
    window.removeEventListener('wheel', rememberUserScroll);
    window.removeEventListener('touchmove', rememberUserScroll);
    window.removeEventListener('keydown', rememberUserScroll);
  };
}

/**
 * SPA / client navigation: Trustpilot is already on the page — init new widgets.
 */
export function initTrustpilotIfReady(root: ParentNode = document): void {
  if (typeof window === 'undefined') return;
  const tp = (window as WindowWithTrustpilot).Trustpilot;
  if (!tp) return;
  loadUnrenderedTrustpilotWidgets(tp, root);
}

/**
 * Every Cookiebot event that means "the consent state is now settled".
 * `OnTagsExecuted` matters most here: it fires *after* Cookiebot has released the
 * tags it was holding back, so `window.Trustpilot` may only appear at that point.
 */
const COOKIEBOT_CONSENT_EVENTS = [
  'CookiebotOnAccept',
  'CookiebotOnDecline',
  'CookiebotOnConsentReady',
  'CookiebotOnTagsExecuted',
] as const;

/**
 * Make widgets that are not showing a live iframe eligible for a fresh round of attempts.
 *
 * Widgets already showing an iframe are left untouched — re-initialising them would flash
 * the iframe.
 */
export function resetUnrenderedTrustpilotWidgets(root: ParentNode = document): void {
  root.querySelectorAll('.trustpilot-widget').forEach((element) => {
    const el = element as HTMLElement;
    if (hasTrustpilotIframe(el)) return;

    dropBlockedTrustpilotIframes(el);
    delete el.dataset.tpLoaded;
    delete el.dataset.tpLoadAttempts;
    delete el.dataset.tpLastAttempt;
    releaseWidget(el);
  });
}

/**
 * Render Trustpilot widgets under `root`, then render them again whenever something could
 * have changed the outcome: the Cookiebot consent state settling, a back/forward restore
 * from the bfcache, or the tab becoming visible again.
 *
 * The retry is what makes this work under `data-blockingmode=auto`: whether Cookiebot
 * neutralised the bootstrap tag or the widget iframe, the element is reset and
 * re-initialised once consent resolves. `initTrustpilotAfterBootstrap` already polls
 * for `window.Trustpilot`, so a bootstrap Cookiebot releases late is still picked up.
 *
 * One attempt runs immediately, so pages without Cookiebot behave exactly as before.
 */
export function initTrustpilotWithConsent(root: ParentNode = document): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let cancelled = false;
  let scheduled = false;
  let disposeInit: (() => void) | null = null;

  const run = () => {
    if (cancelled) return;

    // Drop the previous watcher so only one poll/observer pair is ever live
    disposeInit?.();
    resetUnrenderedTrustpilotWidgets(root);
    disposeInit = initTrustpilotAfterBootstrap(root);
  };

  /**
   * A single accept/decline raises several Cookiebot events in the same tick.
   * Coalesce them into one init; a later `OnTagsExecuted` still gets its own run.
   */
  const schedule = () => {
    if (cancelled || scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      run();
    });
  };

  /**
   * A bfcache restore re-shows the page without remounting React, so nothing else would
   * re-check a widget that was still empty when the user navigated away.
   */
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) schedule();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') schedule();
  };

  COOKIEBOT_CONSENT_EVENTS.forEach((event) => window.addEventListener(event, schedule));
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVisibilityChange);

  // Covers consent that settled before this mount — a cached `uc.js`, or a
  // client-side navigation — where no further event is coming.
  schedule();

  return () => {
    cancelled = true;
    COOKIEBOT_CONSENT_EVENTS.forEach((event) => window.removeEventListener(event, schedule));
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    disposeInit?.();
    disposeInit = null;
  };
}
